// Dashboard Module

import { storage } from './storage.js';
import { dataManager } from './data.js';

class DashboardManager {
  constructor() {
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;

    // Listen for data loaded and workout completed events
    window.addEventListener('dataloaded', () => this.updateDashboard());
    window.addEventListener('datachanged', () => this.updateDashboard());
    window.addEventListener('workoutcompleted', () => this.updateDashboard());

    this.initialized = true;
  }

  updateDashboard() {
    const stats = this.calculateStats();

    const weekWorkoutsEl = document.getElementById('dashWeekWorkouts');
    const streakEl = document.getElementById('dashStreak');
    const volumeEl = document.getElementById('dashVolume');
    const totalEl = document.getElementById('dashTotal');

    if (weekWorkoutsEl) weekWorkoutsEl.textContent = `${stats.weekWorkouts}/4`;
    if (streakEl) streakEl.textContent = `${stats.streak}🔥`;
    if (volumeEl) volumeEl.textContent = `${stats.weekVolume.toLocaleString()}kg`;
    if (totalEl) totalEl.textContent = stats.totalWorkouts;

    this.updateHeatmap();
  }

  calculateStats() {
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday

    let weekWorkouts = 0;
    let totalWorkouts = 0;
    let weekVolume = 0;

    // Count completed workouts
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('workout-completed-')) {
        const dateKey = key + '-date';
        const completionDate = storage.getItem(dateKey) || storage.getItem(key);

        if (completionDate) {
          const date = new Date(completionDate);
          if (date >= weekStart) {
            weekWorkouts++;
          }
          totalWorkouts++;
        }
      }
    }

    // Calculate volume (simplified - from completed exercises)
    const data = dataManager.getData();
    if (data && data.training) {
      for (const week in data.training) {
        for (const day in data.training[week]) {
          const exercises = data.training[week][day];
          exercises.forEach((ex, index) => {
            if (this.isExerciseCompleted(week, day.split(' ')[0], index)) {
              const completionDate = storage.getItem(
                this.getCompletionKey(week, day.split(' ')[0], index) + '-date'
              );
              if (completionDate) {
                const date = new Date(completionDate);
                if (date >= weekStart) {
                  // Extract weight from carico (e.g., "100kg" -> 100)
                  const weight = parseInt(ex.carico) || 0;
                  // Extract reps from serie (e.g., "4x6" -> 6)
                  const reps = parseInt(ex.serie.split('x')[1]) || 0;
                  // Extract sets from serie (e.g., "4x6" -> 4)
                  const sets = parseInt(ex.serie.split('x')[0]) || 0;
                  weekVolume += weight * reps * sets;
                }
              }
            }
          });
        }
      }
    }

    // Calculate streak
    const streak = this.calculateStreak();

    return {
      weekWorkouts,
      totalWorkouts,
      weekVolume,
      streak
    };
  }

  getCompletionKey(week, day, exerciseIndex) {
    return `workout-completion-${week}-${day}-${exerciseIndex}`;
  }

  isExerciseCompleted(week, day, exerciseIndex) {
    const key = this.getCompletionKey(week, day, exerciseIndex);
    // storage.getItem returns boolean true (from JSON.parse), not string 'true'
    return storage.getItem(key) === true;
  }

  calculateStreak() {
    const completedDates = new Set();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('workout-completed-')) {
        const date = storage.getItem(key);
        if (date) {
          completedDates.add(date);
        }
      }
    }

    const sorted = Array.from(completedDates).sort().reverse();
    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    for (const dateStr of sorted) {
      const date = new Date(dateStr);
      date.setHours(0, 0, 0, 0);

      const diffDays = Math.floor((currentDate - date) / (1000 * 60 * 60 * 24));

      if (diffDays === streak) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else if (diffDays > streak) {
        break;
      }
    }

    return streak;
  }

  updateHeatmap() {
    const grid = document.getElementById('heatmapGrid');
    if (!grid) return;

    grid.innerHTML = '';

    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 89); // 90 days ago

    const workoutDates = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('workout-completed-')) {
        const date = storage.getItem(key);
        if (date) {
          workoutDates[date] = (workoutDates[date] || 0) + 1;
        }
      }
    }

    for (let i = 0; i < 90; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      const count = workoutDates[dateStr] || 0;
      let level = 0;
      if (count === 1) level = 1;
      else if (count === 2) level = 2;
      else if (count >= 3) level = 3;

      const day = document.createElement('div');
      day.className = `heatmap-day level-${level}`;
      day.title = `${dateStr}: ${count} workout${count !== 1 ? 's' : ''}`;
      grid.appendChild(day);
    }
  }
}

// Initialize and export
const dashboardManager = new DashboardManager();
dashboardManager.init();

export { dashboardManager };
